import { motion } from 'motion/react';
import { Exchange, Segment } from '@/types/symbols/exchangeTypes';
import { formatAmount, formatToman, getAmount, getPrice } from '@/utils/symbols/exchangeUtils';
import { TableCell, TableRow } from '@/components/ui/table';
import Link from 'next/link';
import RatingBadge from './RatingBadge';
import Image from 'next/image';
import { toPersianDigits } from '@/utils/persianUtils';
import { sitePath } from '@/utils/links/safeHref';

// Create a motion-compatible component from TableRow for animations
const MotionTableRow = motion(TableRow);

interface ExchangeRowProps {
    exchange: Exchange;
    index: number;
    segment: Segment;
    isBestPrice: boolean;
    flash?: 'up' | 'down';
}

const ExchangeRow: React.FC<ExchangeRowProps> = ({
    exchange,
    index,
    segment,
    isBestPrice,
    flash,
}) => {
    const price = getPrice(exchange, segment);
    const amount = getAmount(exchange, segment);
    // Our referral path when the exchange has one (set on the server), else the API's link
    const href = exchange.referralLink || exchange.registerLink || '#';
    const isReferral = /^(https:\/\/khanesarmaye\.com)?\/urls\//.test(href);
    // The first row is the best price right now (rows are sorted by price): highlighted, solid button
    const isTop = index === 0;
    const isBuy = segment === 'buy';
    // Row background lives in the animation (motion's inline colour hid a class): faint green / red
    // for the best row, transparent for the rest; a price change flashes and returns to it.
    const baseBg = isTop
        ? isBuy
            ? 'rgba(16, 185, 129, 0.10)'
            : 'rgba(244, 63, 94, 0.10)'
        : 'rgba(0, 0, 0, 0)';
    const bgAnim =
        flash === 'up'
            ? ['rgba(46, 204, 113, 0.25)', baseBg]
            : flash === 'down'
              ? ['rgba(231, 76, 60, 0.25)', baseBg]
              : baseBg;
    const amountText = amount === 0 ? 'بدون سقف اعلام‌شده' : `${formatAmount(amount)}`;

    return (
        <MotionTableRow
            layout
            className='dark:border-white/10 hover:bg-transparent'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, backgroundColor: bgAnim as any }}
            exit={{ opacity: 0 }}
            transition={{
                backgroundColor: { duration: 1.2 },
                layout: { type: 'spring', stiffness: 400, damping: 30 },
            }}
        >
            {/* Index (desktop only; phones need the width) */}
            <TableCell
                className={`max-sm:hidden text-[11px] text-center opacity-70 w-[36px] min-w-[36px] whitespace-nowrap px-2 ${
                    isTop
                        ? isBuy
                            ? 'border-s-4 border-s-emerald-500/50'
                            : 'border-s-4 border-s-rose-500/50'
                        : ''
                }`}
            >
                {toPersianDigits(index + 1)}
            </TableCell>

            {/* Exchange: logo, name, rating */}
            <TableCell
                className={`py-2.5 ps-3 sm:ps-2 ${
                    isTop
                        ? isBuy
                            ? 'max-sm:border-s-4 max-sm:border-s-emerald-500/50'
                            : 'max-sm:border-s-4 max-sm:border-s-rose-500/50'
                        : ''
                }`}
            >
                <div className='flex items-center gap-2 text-sm min-w-0'>
                    {exchange.logo ? (
                        <Image
                            src={exchange.logo}
                            alt={exchange.name}
                            width={28}
                            height={28}
                            className='size-7 rounded-full object-cover shrink-0 bg-white'
                        />
                    ) : (
                        <div className='size-7 rounded-full bg-gray-200 shrink-0' />
                    )}
                    <div className='flex flex-col items-start min-w-0'>
                        {isTop && (
                            <span
                                className={`mb-0.5 rounded-md px-1.5 py-0.5 text-[9px] sm:text-[10px] font-extrabold whitespace-nowrap ${
                                    isBuy
                                        ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                                        : 'bg-rose-500/10 text-rose-700 dark:text-rose-400'
                                }`}
                            >
                                {isBuy ? 'بهترین خرید الان' : 'بهترین فروش الان'}
                            </span>
                        )}
                        <Link
                            // The `|| '#'` never applied: a missing slug built /exchange/undefined
                            href={sitePath('exchange', exchange?.slug) ?? '#'}
                            className='max-w-full truncate font-bold text-xs sm:text-sm text-gray-800 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors'
                        >
                            {exchange.name}
                        </Link>
                        {exchange.rating && exchange.rating > 0 ? (
                            <RatingBadge rating={exchange.rating} name={exchange.name} />
                        ) : null}
                    </div>
                </div>
            </TableCell>

            {/* Price; on phones the volume sits under it (no volume column, no sideways scroll) */}
            <TableCell className='text-right whitespace-nowrap'>
                <div className='flex flex-col items-start gap-0.5'>
                    <span
                        dir='ltr'
                        className={`text-xs sm:text-sm tabular-nums ${
                            isBestPrice
                                ? isBuy
                                    ? 'font-black text-emerald-600 dark:text-emerald-400'
                                    : 'font-black text-rose-600 dark:text-rose-400'
                                : 'font-bold text-gray-800 dark:text-gray-100'
                        }`}
                    >
                        {formatToman(price)}
                    </span>
                    <span
                        className='sm:hidden text-[10px] text-gray-500 dark:text-gray-400'
                        title='بیشترین مقداری که صرافی در همین قیمت می‌فروشد یا می‌خرد'
                    >
                        حجم: <bdi>{amountText}</bdi>
                    </span>
                </div>
            </TableCell>

            {/* Volume (tablet/desktop) */}
            <TableCell className='max-sm:hidden text-right text-xs whitespace-nowrap text-gray-600 dark:text-gray-300'>
                {/* 0 = the exchange reports no limit for an order at this price */}
                {amount === 0 ? (
                    <span title='صرافی برای سفارش در این قیمت سقف حجمی اعلام نکرده است'>
                        {amountText}
                    </span>
                ) : (
                    <span dir='ltr'>{amountText}</span>
                )}
            </TableCell>

            {/* Action: solid for the best row, quiet for the rest */}
            <TableCell className='text-center whitespace-nowrap w-[64px] sm:w-[90px] pe-3 sm:pe-2'>
                <a
                    href={href}
                    target='_blank'
                    rel={isReferral ? 'sponsored nofollow noopener' : 'noopener noreferrer'}
                    className={`inline-flex items-center justify-center rounded-lg sm:rounded-xl border px-3 sm:px-4 py-1.5 text-xs font-bold transition-all duration-200 active:scale-95 ${
                        isTop
                            ? isBuy
                                ? 'border-emerald-500 bg-emerald-500 text-white shadow-xs hover:bg-emerald-600'
                                : 'border-rose-500 bg-rose-500 text-white shadow-xs hover:bg-rose-600'
                            : isBuy
                              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300'
                              : 'border-rose-500/30 bg-rose-500/10 text-rose-700 hover:bg-rose-500/20 dark:text-rose-300'
                    }`}
                >
                    {isBuy ? 'خرید' : 'فروش'}
                </a>
            </TableCell>
        </MotionTableRow>
    );
};

export default ExchangeRow;
